import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { FileText, Download, Printer, X, Calendar } from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";



const formatCurrency = (value) => {
  return (value || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("tr-TR");
};

const AccountMovements = ({ 
  open, 
  onClose, 
  accountType, // "customer", "supplier", "cash", "bank", "credit_card"
  accountId,
  accountName
}) => {
  const [movements, setMovements] = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (open && accountId) {
      // Set default dates when modal opens (if not already set)
      if (!startDate && !endDate) {
        // Default: Show all movements from beginning of current year
        const currentYear = new Date().getFullYear();
        const yearStart = `${currentYear}-01-01`;
        const today = new Date().toISOString().split("T")[0];
        setStartDate(yearStart);
        setEndDate(today);
      } else {
        fetchMovements();
      }
    }
  }, [open, accountId]);

  useEffect(() => {
    // Fetch movements when dates change (after initial load)
    if (open && accountId && (startDate || endDate)) {
      fetchMovements();
    }
  }, [startDate, endDate]);

  const getEndpointUrl = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    const queryString = params.toString() ? `?${params.toString()}` : "";

    switch (accountType) {
      case "customer":
        return `${API}/customers/${accountId}/movements${queryString}`;
      case "supplier":
        return `${API}/suppliers/${accountId}/movements${queryString}`;
      case "cash":
        return `${API}/cash-accounts/${accountId}/movements${queryString}`;
      case "bank":
        return `${API}/bank-accounts/${accountId}/movements${queryString}`;
      case "credit_card":
        return `${API}/credit-cards/${accountId}/movements${queryString}`;
      default:
        return null;
    }
  };

  const fetchMovements = async () => {
    const url = getEndpointUrl();
    if (!url) return;

    setLoading(true);
    try {
      const response = await axios.get(url);
      setMovements(response.data.movements || []);
      setAccountInfo(response.data.customer || response.data.supplier || response.data.account || response.data.card);
      setSummary(response.data.summary);
    } catch (error) {
      console.error("Failed to fetch movements:", error);
      toast.error("Hesap hareketleri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const getAccountTypeLabel = () => {
    switch (accountType) {
      case "customer": return "Müşteri";
      case "supplier": return "Tedarikçi";
      case "cash": return "Kasa";
      case "bank": return "Banka";
      case "credit_card": return "Kredi Kartı";
      default: return "Hesap";
    }
  };

  const isDebitCredit = accountType === "customer" || accountType === "supplier";

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Başlık
    doc.setFontSize(18);
    doc.text("HESAP EKSTRESİ", 105, 20, { align: "center" });
    
    // Hesap bilgileri
    doc.setFontSize(11);
    doc.text(`${getAccountTypeLabel()}: ${accountName}`, 14, 35);
    
    if (accountInfo?.account_number) {
      doc.text(`Cari Hesap No: ${accountInfo.account_number}`, 14, 42);
    }
    
    const dateRange = startDate || endDate 
      ? `Tarih Aralığı: ${startDate || "Başlangıç"} - ${endDate || "Bitiş"}`
      : "Tüm Hareketler";
    doc.text(dateRange, 14, 49);
    doc.text(`Oluşturma Tarihi: ${new Date().toLocaleDateString("tr-TR")}`, 14, 56);

    // Tablo
    const tableColumn = isDebitCredit 
      ? ["Tarih", "Açıklama", "Belge No", "Borç", "Alacak", "Bakiye"]
      : ["Tarih", "Açıklama", "Belge No", "Giriş", "Çıkış", "Bakiye"];
    
    const tableRows = movements.map(mov => [
      formatDate(mov.date),
      mov.description,
      mov.document_no || "-",
      isDebitCredit 
        ? (mov.debit > 0 ? formatCurrency(mov.debit) : "-")
        : (mov.inflow > 0 ? formatCurrency(mov.inflow) : "-"),
      isDebitCredit 
        ? (mov.credit > 0 ? formatCurrency(mov.credit) : "-")
        : (mov.outflow > 0 ? formatCurrency(mov.outflow) : "-"),
      formatCurrency(mov.balance)
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 65,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [51, 51, 51] },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 60 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25, halign: "right" },
        4: { cellWidth: 25, halign: "right" },
        5: { cellWidth: 25, halign: "right" }
      }
    });

    // Özet
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    
    if (isDebitCredit) {
      doc.text(`Toplam Borç: ${formatCurrency(summary?.total_debit)} TL`, 14, finalY);
      doc.text(`Toplam Alacak: ${formatCurrency(summary?.total_credit)} TL`, 14, finalY + 7);
      doc.text(`Bakiye: ${formatCurrency(summary?.balance)} TL`, 14, finalY + 14);
    } else {
      doc.text(`Toplam Giriş: ${formatCurrency(summary?.total_inflow)} TL`, 14, finalY);
      doc.text(`Toplam Çıkış: ${formatCurrency(summary?.total_outflow)} TL`, 14, finalY + 7);
      doc.text(`Bakiye: ${formatCurrency(summary?.balance)} TL`, 14, finalY + 14);
    }

    return doc;
  };

  const handleDownloadPDF = () => {
    const doc = generatePDF();
    doc.save(`hesap-ekstresi-${accountName.replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF indirildi");
  };

  const handlePrint = () => {
    const doc = generatePDF();
    doc.autoPrint();
    window.open(doc.output("bloburl"), "_blank");
  };

  const handleClose = () => {
    // Reset dates when modal closes
    setStartDate("");
    setEndDate("");
    setMovements([]);
    setSummary(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl mx-4 sm:mx-auto max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {getAccountTypeLabel()} Hesap Hareketleri - {accountName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-shrink-0 flex flex-wrap items-end gap-4 pb-4 border-b">
          <div className="space-y-1">
            <Label className="text-xs">Başlangıç Tarihi</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Bitiş Tarihi</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setStartDate(""); setEndDate(""); }}>
            <X className="h-4 w-4 mr-1" /> Filtreyi Temizle
          </Button>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={movements.length === 0}>
              <Download className="h-4 w-4 mr-1" /> PDF İndir
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={movements.length === 0}>
              <Printer className="h-4 w-4 mr-1" /> Yazdır
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : movements.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Tarih</TableHead>
                  <TableHead className="font-semibold">Açıklama</TableHead>
                  <TableHead className="font-semibold">Belge No</TableHead>
                  <TableHead className="font-semibold text-right">
                    {isDebitCredit ? "Borç" : "Giriş"}
                  </TableHead>
                  <TableHead className="font-semibold text-right">
                    {isDebitCredit ? "Alacak" : "Çıkış"}
                  </TableHead>
                  <TableHead className="font-semibold text-right">Bakiye</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((mov, index) => (
                  <TableRow key={index}>
                    <TableCell className="whitespace-nowrap">{formatDate(mov.date)}</TableCell>
                    <TableCell>{mov.description}</TableCell>
                    <TableCell className="font-mono text-sm">{mov.document_no || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-600">
                      {isDebitCredit 
                        ? (mov.debit > 0 ? `₺${formatCurrency(mov.debit)}` : "-")
                        : (mov.inflow > 0 ? `₺${formatCurrency(mov.inflow)}` : "-")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">
                      {isDebitCredit 
                        ? (mov.credit > 0 ? `₺${formatCurrency(mov.credit)}` : "-")
                        : (mov.outflow > 0 ? `₺${formatCurrency(mov.outflow)}` : "-")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      ₺{formatCurrency(mov.balance)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <Calendar className="h-12 w-12 mb-2 text-slate-300" />
              <p>Bu tarih aralığında hareket bulunamadı</p>
            </div>
          )}
        </div>

        {summary && (
          <div className="flex-shrink-0 pt-4 border-t bg-slate-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-500 mb-1">
                  {isDebitCredit ? "Toplam Borç" : "Toplam Giriş"}
                </p>
                <p className="text-lg font-semibold text-emerald-600 tabular-nums">
                  ₺{formatCurrency(isDebitCredit ? summary.total_debit : summary.total_inflow)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">
                  {isDebitCredit ? "Toplam Alacak" : "Toplam Çıkış"}
                </p>
                <p className="text-lg font-semibold text-red-600 tabular-nums">
                  ₺{formatCurrency(isDebitCredit ? summary.total_credit : summary.total_outflow)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Bakiye</p>
                <p className={`text-lg font-semibold tabular-nums ${summary.balance >= 0 ? "text-slate-900" : "text-red-600"}`}>
                  ₺{formatCurrency(summary.balance)}
                </p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccountMovements;
